import { CMS_API_URL, DEFAULT_LOCALE } from '@/lib/site-config'
import type { Entry, Menu, PreviewTarget, Site } from '@/lib/types'

/**
 * Anything the CMS said, or failed to say, that a page has to render around.
 *
 * `status` is what a caller branches on — never `message`, which the contract
 * describes as human-readable text for a log. A transport failure becomes
 * `503`, because "the CMS is unreachable" and "this page does not exist" are
 * different things to put in front of a visitor.
 */
export class CmsError extends Error {
    constructor(
        readonly status: number,
        message: string,
    ) {
        super(message)
        this.name = 'CmsError'
    }
}

type Query = Record<string, string | number | undefined>

/** Next reads `next.revalidate`; the type is not in the DOM's RequestInit. */
type FetchInit = RequestInit & { next?: { revalidate: number } }

/**
 * How hard to lean on a CMS that has asked us to wait.
 *
 * A build renders every page there is, several at a time, and the API throttles
 * per minute — so a site with enough entries will hit the limit on its own
 * build, and giving up there means shipping a site with pages missing. The same
 * applies at runtime to a revalidation that lands in a busy minute.
 */
const RETRY_STATUSES = new Set([429, 502, 503, 504])
const RETRIES = 8
const RETRY_BASE_MS = 700

/**
 * The longest we will sit on a single `Retry-After`.
 *
 * A per-minute limiter's window is at most a minute, so this has to clear
 * sixty seconds — capped below it, every wait ends *before* the window resets
 * and the retry is guaranteed to be refused, which is how a build burns four
 * attempts in a row and fails anyway. Above a minute the answer is no longer a
 * rate limit and waiting is not the fix.
 */
const RETRY_MAX_MS = 65_000

/**
 * How long to wait before trying again.
 *
 * `Retry-After` is the server's own answer, in seconds or as a date, and it
 * wins whenever it is present — a rate limiter knows when its window resets and
 * we would only be guessing. The fallback backs off exponentially, with jitter
 * so that a build's parallel workers do not all come back at the same instant
 * and trip the limit a second time.
 */
function retryDelay(response: Response, attempt: number): number {
    const header = response.headers.get('retry-after')

    if (header !== null) {
        const seconds = Number(header)
        const wait = Number.isNaN(seconds) ? Date.parse(header) - Date.now() : seconds * 1000

        if (Number.isFinite(wait) && wait > 0) {
            // Jittered like the fallback below, and for the same reason: every
            // worker held by one window is told to come back at the same
            // instant, and arriving together is what tripped the limit.
            return Math.min(wait, RETRY_MAX_MS) + 250 + Math.random() * 400
        }
    }

    return RETRY_BASE_MS * 2 ** attempt + Math.random() * 400
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

function url(path: string, query: Query): string {
    const target = new URL(`${CMS_API_URL.replace(/\/$/, '')}${path}`)

    for (const [key, value] of Object.entries(query)) {
        if (value !== undefined && value !== '') {
            target.searchParams.set(key, String(value))
        }
    }

    return target.toString()
}

/** Omitting it means the default locale, which is what the API already does. */
function localeParam(locale?: string): string | undefined {
    return locale === undefined || locale === DEFAULT_LOCALE ? undefined : locale
}

/**
 * One request, returning the whole envelope.
 *
 * A listing needs `meta` as well as `data`, so unwrapping happens in the
 * callers that only want `data` rather than here.
 */
async function envelope<T>(path: string, query: Query, token?: string): Promise<T> {
    // A token makes the response private and uncacheable at both ends: the
    // API sends `no-store` for it, and asking Next to cache it anyway would
    // leak a draft into a render shared with everyone else.
    const init: FetchInit = token === undefined ? { next: { revalidate: 60 } } : { cache: 'no-store' }

    const target = url(path, { ...query, token })

    let response: Response

    for (let attempt = 0; ; attempt++) {
        try {
            response = await fetch(target, init)
        } catch (error) {
            throw new CmsError(503, `Could not reach the CMS at ${CMS_API_URL}: ${String(error)}`)
        }

        if (!RETRY_STATUSES.has(response.status) || attempt >= RETRIES) {
            break
        }

        await sleep(retryDelay(response, attempt))
    }

    const body: unknown = await response.json().catch(() => null)

    if (!response.ok) {
        const message =
            body !== null && typeof body === 'object' && 'message' in body
                ? String((body as { message: unknown }).message)
                : response.statusText

        throw new CmsError(response.status, message)
    }

    return body as T
}

async function request<T>(path: string, query: Query, token?: string): Promise<T> {
    const body = await envelope<{ data: T }>(path, query, token)

    return body.data
}

/** Null on 404: a missing entry is a page state, not an exception. */
export async function getEntry(
    type: string,
    slug: string,
    options: { locale?: string; token?: string } = {},
): Promise<Entry | null> {
    try {
        return await request<Entry>(
            `/entries/${type}/${slug}`,
            { locale: localeParam(options.locale) },
            options.token,
        )
    } catch (error) {
        if (error instanceof CmsError && error.status === 404) {
            return null
        }

        throw error
    }
}

export async function listEntries(options: {
    type?: string
    locale?: string
    cursor?: string
    perPage?: number
    /** One of the API's documented sorts, e.g. `order` or `-published_at`. */
    sort?: string
}): Promise<{ data: Entry[]; nextCursor: string | null }> {
    const page = await envelope<{ data: Entry[]; meta?: { next_cursor?: string | null } }>('/entries', {
        type: options.type,
        locale: localeParam(options.locale),
        cursor: options.cursor,
        per_page: options.perPage,
        sort: options.sort,
    })

    // Cursors are opaque and their encoding is not part of the contract. This
    // carries the value forward and hands it back; it never builds one.
    return { data: page.data, nextCursor: page.meta?.next_cursor ?? null }
}

/**
 * The site's own settings.
 *
 * The string fields are defaulted rather than trusted. A CMS that is a release
 * behind — which is precisely the case during a deploy, when this build reads
 * the API of the release it is replacing — serves a payload without the newest
 * keys, and a footer that reads `.replace` off one of them takes the build down
 * with it.
 */
export async function getSite(locale?: string): Promise<Site> {
    const site = await request<Site>('/site', { locale: localeParam(locale) })

    return {
        ...site,
        legal_name: site.legal_name || site.name,
        email: site.email ?? '',
        phone: site.phone ?? '',
        address: site.address ?? '',
        coc_number: site.coc_number ?? '',
        vat_number: site.vat_number ?? '',
        iban: site.iban ?? '',
    }
}

/** Null on 404, so a layout renders without navigation rather than 500ing. */
export async function getMenu(location: string, locale?: string): Promise<Menu | null> {
    try {
        return await request<Menu>(`/menus/${location}`, { locale: localeParam(locale) })
    } catch (error) {
        if (error instanceof CmsError && error.status === 404) {
            return null
        }

        throw error
    }
}

/**
 * Null for the one 401 that every invalid, expired, tampered, unknown and
 * deleted token shares. The API refuses to distinguish them on purpose —
 * telling them apart would make this endpoint an oracle for which entry ids
 * exist — and this function refuses to invent a distinction it withheld.
 */
export async function validatePreviewToken(token: string): Promise<PreviewTarget | null> {
    try {
        return await request<PreviewTarget>('/preview/validate', {}, token)
    } catch (error) {
        if (error instanceof CmsError && (error.status === 401 || error.status === 422)) {
            return null
        }

        throw error
    }
}
